import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/utils/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, leftIcon, rightIcon, id, ...props }, ref) => {
    const inputId = id ?? props.name;
    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1.5 block text-body-sm font-medium text-on-surface">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'h-10 w-full rounded-DEFAULT border bg-surface px-3 text-data-md text-on-surface',
              'placeholder:text-on-surface-variant/70 transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary/40',
              'disabled:cursor-not-allowed disabled:bg-surface-variant disabled:opacity-60',
              leftIcon && 'pl-9',
              rightIcon && 'pr-9',
              error ? 'border-danger focus:ring-danger/30' : 'border-outline',
              className,
            )}
            aria-invalid={!!error}
            {...props}
          />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant">
              {rightIcon}
            </span>
          )}
        </div>
        {error ? (
          <p className="mt-1 text-body-sm text-danger">{error}</p>
        ) : hint ? (
          <p className="mt-1 text-body-sm text-on-surface-variant">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
